import {BehaviorSubject, defer, type Observable} from 'rxjs';
import {share, shareReplay, finalize} from 'rxjs/operators';

/**
 * 在原始的 observable 上增加 sub/unsub 的 hook
 * 默认使用 share 实现
 * 需要 shareReplay 请使用 genShareReplyWithSubscribeHook
 *
 * ## 效果:
 *  - 当流被一次或者多次订阅时，触发一次 subHook
 *  - 当全部订阅都取消时，触发一次 unsubHook
 *
 * ## Example
 * ```ts
 *  genObservableWithSubscribeHook(
 *    raw$,
 *    () => {
 *      wwData$.send({type: 'dynamic-subscribe', method: 'subscribeLendingAccount'});
 *    },
 *    () => {
 *      // worker 不支持取消订阅的行为，所以这里只是示例
 *      wwData$.send({type: 'dynamic-unsubscribe', method: 'subscribeLendingAccount'});
 *    }
 *  )
 *  ```
 */
export function genObservableWithSubscribeHook<T>(rawObservable$: Observable<T>, subHook?: Function, unsubHook?: Function) {
  return defer(() => {
    subHook && subHook();
    return rawObservable$;
  }).pipe(
    share({
      resetOnRefCountZero: () => {
        unsubHook && unsubHook();
        return new BehaviorSubject(1);
      },
    })
  );
}

/**
 * genObservableWithSubscribeHook 的 shareReplay 版本
 */
export function genShareReplyWithSubscribeHook<T>(rawObservable$: Observable<T>, subHook?: Function, unsubHook?: Function) {
  const shareObservable$ = rawObservable$.pipe(shareReplay(1));

  return defer(() => {
    subHook && subHook();
    return shareObservable$;
  }).pipe(
    shareReplay({bufferSize: 1, refCount: true}),
    finalize(() => {
      unsubHook && unsubHook();
    })
  );
}
